import { Badge, Button, Group, Paper, Stack, Text, UnstyledButton } from '@mantine/core';
import { useNavigate } from 'react-router';
import { useCalendarEvents } from 'src/meetings/hooks/useCalendarEvents';
import type { CalendarEvent } from 'src/meetings/models';
import { formatShortDate, formatTime } from 'src/utils/dateFormatting';
import { formatName, NameFormat } from 'src/utils/nameFormatting';

const WINDOW_HOURS = 48;

function eventClient(event: CalendarEvent) {
  return (
    event.eventParticipants
      .map(eventParticipant => eventParticipant.participant.client)
      .find(client => client !== null) ?? null
  );
}

function dayLabel(value: string): string {
  const date = new Date(value);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return formatShortDate(date);
}

export default function UpcomingMeetingsRail() {
  const navigate = useNavigate();
  const { events, isLoading } = useCalendarEvents({
    pastOrUpcoming: 'UPCOMING',
    clientMeetingsOnly: true,
  });

  const windowEnd = new Date();
  windowEnd.setHours(windowEnd.getHours() + WINDOW_HOURS);
  const imminent = events.filter(event => new Date(event.scheduledStartAt) <= windowEnd);

  if (!isLoading && imminent.length === 0) {
    return (
      <Text size="sm" c="gray.6">
        No client meetings in the next {WINDOW_HOURS} hours.
      </Text>
    );
  }

  return (
    <Stack gap="xs">
      {imminent.map(event => {
        const client = eventClient(event);
        const viewDestination = client
          ? `/client/${client.id}/meetings/upcoming/${event.id}`
          : `/meetings?eventId=${event.id}`;
        return (
          <UnstyledButton key={event.id} onClick={() => navigate(viewDestination)}>
            <Paper withBorder radius="md" p="sm">
              <Group justify="space-between" wrap="nowrap" gap="xs">
                <Stack gap={2} style={{ minWidth: 0 }}>
                  <Text size="sm" fw={600} truncate>
                    {event.title}
                  </Text>
                  <Text size="xs" c="gray.6">
                    {dayLabel(event.scheduledStartAt)}, {formatTime(event.scheduledStartAt)}
                    {client && (
                      <>
                        {' '}
                        &middot; {formatName(client.firstName, client.lastName, NameFormat.NATURAL)}
                      </>
                    )}
                  </Text>
                </Stack>
                {event.hasMeetingAgenda ? (
                  <Badge color="teal" variant="light" radius="sm">
                    Agenda ready
                  </Badge>
                ) : (
                  <Group gap="xs" wrap="nowrap">
                    <Badge color="orange" variant="light" radius="sm">
                      Needs agenda
                    </Badge>
                    {client && (
                      <Button
                        size="xs"
                        variant="default"
                        onClick={mouseEvent => {
                          mouseEvent.stopPropagation();
                          navigate(
                            `/client/${client.id}/meetings/upcoming/${event.id}?generate_agenda=true`,
                          );
                        }}
                      >
                        Generate
                      </Button>
                    )}
                  </Group>
                )}
              </Group>
            </Paper>
          </UnstyledButton>
        );
      })}
    </Stack>
  );
}
